import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService, MapType } from '../services/apiService';
import './MapVisualization.css';

interface MapVisualizationProps {
  mapType: MapType;
  title: string;
  description: string;
  customerId: string;
  /** Plot code to zoom to / highlight on the map. */
  focus?: string;
  /** Changes whenever the parent asks this map to open (e.g. a row's "Map" button). */
  openRequest?: number;
  /** Called when the user clears the plot focus. */
  onClearFocus?: () => void;
}

const MapVisualization: React.FC<MapVisualizationProps> = ({
  mapType,
  title,
  description,
  customerId,
  focus,
  openRequest,
  onClearFocus,
}) => {
  const [mapHtml, setMapHtml] = useState<string>('');
  const [loadedFocus, setLoadedFocus] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const loadMapData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const htmlContent = await apiService.getMapData(customerId, mapType, focus);
      setMapHtml(htmlContent);
      setLoadedFocus(focus);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map data');
      console.error('Error loading map data:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId, mapType, focus]);

  // Load when expanded, or reload when the requested focus differs from the loaded map.
  useEffect(() => {
    if (isExpanded && !loading && (!mapHtml || loadedFocus !== focus)) {
      loadMapData();
    }
  }, [isExpanded, mapHtml, focus, loadedFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parent asked to open this map (row "Map" action): expand and scroll into view.
  useEffect(() => {
    if (!openRequest) return;
    setIsExpanded(true);
    if (rootRef.current && typeof rootRef.current.scrollIntoView === 'function') {
      rootRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [openRequest]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const getMapIcon = (type: string) => {
    switch (type) {
      case 'overlapping-plots':
        return '●';
      case 'protected-area-overlap':
        return '■';
      case 'forest-cover-overlap':
        return '▲';
      case 'forest-reserve-overlap':
        return '▬';
      case 'all-layers':
        return '◈';
      default:
        return '◆';
    }
  };

  const refreshMap = () => {
    setMapHtml('');
    loadMapData();
  };

  return (
    <div className="map-visualization" ref={rootRef}>
      <div
        className="map-header"
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <div className="map-header-content">
          <div className="map-icon" aria-hidden="true">{getMapIcon(mapType)}</div>
          <div className="map-info">
            <h3>{title}</h3>
            <p>{description}</p>
            {focus && (
              <div className="map-focus-badge">
                Focused on plot <strong>{focus}</strong>
                {onClearFocus && (
                  <button
                    type="button"
                    className="map-focus-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearFocus();
                    }}
                    aria-label={`Clear map focus on plot ${focus}`}
                  >
                    ✕ Clear
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="map-controls">
          {isExpanded && mapHtml && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                refreshMap();
              }}
              className="refresh-btn"
              disabled={loading}
            >
              ↻ Refresh
            </button>
          )}
          <button
            type="button"
            className="expand-btn"
            aria-label={isExpanded ? `Collapse ${title} map` : `Expand ${title} map`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded();
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="map-content">
          {loading && (
            <div className="map-loading">
              <div className="spinner"></div>
              <span>Loading map...</span>
            </div>
          )}

          {error && (
            <div className="map-error">
              <span>✗ {error}</span>
              <button onClick={loadMapData} className="retry-btn">
                Retry
              </button>
            </div>
          )}

          {mapHtml && !loading && !error && (
            <div className="map-container">
              <iframe
                key={loadedFocus || ''}
                srcDoc={mapHtml}
                title={focus ? `${title} – plot ${focus}` : title}
                className="map-iframe"
                sandbox="allow-scripts"
              />
              <div className="map-fullscreen">
                <button
                  onClick={() => {
                    const newWindow = window.open('', '_blank');
                    if (newWindow) {
                      newWindow.document.write(mapHtml);
                      newWindow.document.close();
                    }
                  }}
                  className="fullscreen-btn"
                >
                  🔍 Open in New Window
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapVisualization;
